import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Paper,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  InputAdornment,
  Menu,
  MenuItem,
  Autocomplete,
  FormControlLabel,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Checkbox,
  Tooltip,
  TablePagination,
  Link
} from '@mui/material';
import {
  Receipt,
  LocalShipping,
  Print,
  Add as AddIcon,
  Search,
  FilterList,
  MoreVert,
  ViewColumn,
  Close
} from '@mui/icons-material';
import { orderService } from '../services/orderService';
import { quoteService } from '../services/quoteService';
import { customerService } from '../services/customerService';
import { packingSlipService } from '../services/packingSlipService';
import { format } from 'date-fns';
import { formatDateOnly, formatLocalTodayYmd, parseLocalDate } from '../utils/dateUtils';
import { formatCurrency } from '../components/CurrencyTextField';
import '../styles/print.css';
import PrintPage from '../components/print/PrintPage';
import ResizableHeaderCell from '../components/ResizableHeaderCell';
import { useResizableColumns } from '../hooks/useResizableColumns';
import { printWithFilename } from '../utils/printUtils';
import NewCustomerModal from '../components/NewCustomerModal';

const NEW_CUSTOMER_OPTION = { id: '__new_customer__', __isNew: true };

const tmCustomerLabel = (c) =>
  c?.__isNew
    ? '+ New customer'
    : c?.company_name ||
      [c?.first_name, c?.last_name].filter(Boolean).join(' ') ||
      (c?.customer_number != null ? `Customer #${c.customer_number}` : '') ||
      (c?.id != null ? `ID ${c.id}` : '');

const WorkOrders = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const customerFilter = searchParams.get('customer');
  const partFilter = searchParams.get('part_id');
  const [showCancelledOrders, setShowCancelledOrders] = useState(() => {
    try {
      return window.localStorage.getItem('wm_orders_show_cancelled') === 'true';
    } catch {
      return false;
    }
  });
  const [showSupersededOrders, setShowSupersededOrders] = useState(() => {
    try {
      return window.localStorage.getItem('wm_orders_show_superseded') === 'true';
    } catch {
      return false;
    }
  });
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tableSearch, setTableSearch] = useState('');
  /** Debounced value used for API list + pagination (avoids a request per keystroke). */
  const [debouncedTableSearch, setDebouncedTableSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedOrderForHeader, setSelectedOrderForHeader] = useState(null);
  const [printMenuAnchor, setPrintMenuAnchor] = useState(null);
  const [colDateFrom, setColDateFrom] = useState('');
  const [colDateTo, setColDateTo] = useState('');
  const [packingSlipBusyOrderId, setPackingSlipBusyOrderId] = useState(null);
  const [packingSlipByOrderId, setPackingSlipByOrderId] = useState({});
  const [sortKey, setSortKey] = useState('order_date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [newTmDialogOpen, setNewTmDialogOpen] = useState(false);
  const [tmCustomer, setTmCustomer] = useState(null);
  const [tmDescription, setTmDescription] = useState('');
  const [tmPoNumber, setTmPoNumber] = useState('');
  const [tmSaving, setTmSaving] = useState(false);
  const [tmCustomers, setTmCustomers] = useState([]);
  const [newOrderStyle, setNewOrderStyle] = useState('standard');
  const [newCustomerModalOpen, setNewCustomerModalOpen] = useState(false);

  // Aligned IDs: YYO-##### or YYQ-#####; revisions are -2, -3, … (not YYO-##### alone — that has a false match with /.+-\d+$/)
  const hasOrderRevisionSuffix = (identifier) => {
    const m = String(identifier || '').trim().match(/^(\d{2})O-(\d{5,})(?:-(\d+))?(?:r\d+)?$/i);
    return !!(m && m[3] != null && parseInt(m[3], 10) >= 2);
  };
  const hasQuoteRevisionSuffix = (identifier) => {
    const m = String(identifier || '').trim().match(/^(\d{2})Q-(\d{5,})(?:-(\d+))?(?:r\d+)?$/i);
    return !!(m && m[3] != null && parseInt(m[3], 10) >= 2);
  };
  const DEFAULT_ORDER_COL_WIDTHS = {
    order: 180,
    quote: 120,
    date: 110,
    time: 90,
    customer: 240,
    customerPo: 140,
    total: 120,
    salesman: 150,
    status: 120,
    notes: 280,
    actions: 120
  };
  const {
    colWidths,
    setColWidths,
    startResize,
    saveColWidths,
    resetColWidths
  } = useResizableColumns({
    defaultWidths: DEFAULT_ORDER_COL_WIDTHS,
    storageKey: 'wm_orders_col_widths',
    minWidths: {
      order: 120,
      quote: 100,
      date: 90,
      time: 80,
      customer: 160,
      customerPo: 110,
      total: 100,
      salesman: 120,
      status: 100,
      notes: 160,
      actions: 100
    }
  });
  const [columnsDialogOpen, setColumnsDialogOpen] = useState(false);
  const [revisionDialogOpen, setRevisionDialogOpen] = useState(false);
  const [revisionDialogTitle, setRevisionDialogTitle] = useState('');
  const [revisionDialogItems, setRevisionDialogItems] = useState([]);
  const [revisionDialogLoading, setRevisionDialogLoading] = useState(false);
  const [revisionDialogType, setRevisionDialogType] = useState('order'); // 'order' | 'quote'

  const handleSort = (key) => {
    setSortDirection((prevDir) => (sortKey === key ? (prevDir === 'asc' ? 'desc' : 'asc') : 'asc'));
    setSortKey(key);
  };

  useEffect(() => {
    if (!newTmDialogOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await customerService.getAll({ limit: 500, activeOnly: true });
        const list = res?.data ?? [];
        if (!cancelled) setTmCustomers(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error(e);
        if (!cancelled) setTmCustomers([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [newTmDialogOpen]);

  const handleCreateTmOrder = async () => {
    if (!tmCustomer?.id) {
      setError('Select a customer.');
      return;
    }
    try {
      setTmSaving(true);
      setError('');
      const res = await orderService.create({
        customer_id: tmCustomer.id,
        order_type: 'STANDARD',
        order_style: newOrderStyle,
        description: tmDescription?.trim() || null,
        po_number: tmPoNumber?.trim() || null,
        order_date: formatLocalTodayYmd(),
        tax_mode: 'none',
        tax_basis: 'document',
        lineItems: []
      });
      const created = res?.data;
      setNewTmDialogOpen(false);
      setTmCustomer(null);
      setTmDescription('');
      setTmPoNumber('');
      setNewOrderStyle('standard');
      if (created?.id) {
        navigate(`/work-orders/${created.id}`);
      }
      await loadOrders();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to create order');
    } finally {
      setTmSaving(false);
    }
  };

  const filteredOrders = useMemo(() => {
    let list = orders;
    if (colDateFrom) {
      const from = parseLocalDate(colDateFrom);
      if (from) list = list.filter((o) => {
        const od = o.order_date ? parseLocalDate(o.order_date) : null;
        return od ? od.getTime() >= from.getTime() : false;
      });
    }
    if (colDateTo) {
      const to = parseLocalDate(colDateTo);
      if (to) list = list.filter((o) => {
        const od = o.order_date ? parseLocalDate(o.order_date) : null;
        return od ? od.getTime() <= to.getTime() : false;
      });
    }
    const getSortValue = (o) => {
      switch (sortKey) {
        case 'order_identifier':
          return String(o.identifier ?? o.work_order_number ?? o.work_order_id ?? '');
        case 'quote_identifier':
          return String(o.quote_identifier ?? o.quote_number ?? o.quote_id ?? '');
        case 'order_date':
          return o.order_date
            ? (parseLocalDate(o.order_date)?.getTime() ?? 0)
            : (o.created_at ? new Date(o.created_at).getTime() : 0);
        case 'time':
          return o.created_at ? new Date(o.created_at).getTime() : 0;
        case 'customer_name':
          return String(o.company_name || [o.customer_first_name, o.customer_last_name].filter(Boolean).join(' ') || '').toLowerCase();
        case 'customer_po':
          return String(o.po_number ?? '');
        case 'total_amount':
          return o.total_amount != null ? Number(o.total_amount) : 0;
        case 'salesman':
          return String(`${o.salesman_first_name || ''} ${o.salesman_last_name || ''}`.trim() || o.salesman_name || '').toLowerCase();
        case 'status':
          return String(o.status ?? '').toLowerCase();
        case 'notes':
          return String(o.description || o.notes || '').toLowerCase();
        default:
          return '';
      }
    };

    const dir = sortDirection === 'asc' ? 1 : -1;
    list = [...list].sort((a, b) => {
      const va = getSortValue(a);
      const vb = getSortValue(b);
      if (typeof va === 'number' && typeof vb === 'number') return dir * (va - vb);
      return dir * String(va ?? '').localeCompare(String(vb ?? ''), undefined, { numeric: true, sensitivity: 'base' });
    });

    return list;
  }, [orders, colDateFrom, colDateTo, sortKey, sortDirection]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedTableSearch(tableSearch.trim()), 350);
    return () => clearTimeout(t);
  }, [tableSearch]);

  const ordersFilterKeyRef = useRef(null);

  const loadOrders = useCallback(async () => {
    const filterKey = [
      statusFilter,
      customerFilter || '',
      partFilter || '',
      String(showCancelledOrders),
      String(showSupersededOrders),
      debouncedTableSearch
    ].join('|');
    const filterJustChanged =
      ordersFilterKeyRef.current != null && ordersFilterKeyRef.current !== filterKey;
    ordersFilterKeyRef.current = filterKey;

    const effectivePage = filterJustChanged ? 0 : page;
    if (filterJustChanged && page !== 0) {
      setPage(0);
    }

    try {
      setLoading(true);
      setError('');
      const params = {
        limit: rowsPerPage,
        offset: effectivePage * rowsPerPage,
        search: debouncedTableSearch,
        status: statusFilter || undefined,
        ...(customerFilter ? { customerId: customerFilter } : {}),
        ...(partFilter ? { part_id: partFilter } : {}),
        ...(showCancelledOrders ? { include_cancelled: 'true' } : {}),
        ...(showSupersededOrders ? { include_superseded: 'true' } : {})
      };
      const response = await orderService.getAll(params);
      const list = Array.isArray(response?.data) ? response.data : [];
      const total = typeof response?.total === 'number' ? response.total : list.length;
      const maxPage = Math.max(0, Math.ceil(total / rowsPerPage) - 1);
      if (effectivePage > maxPage) {
        setPage(maxPage);
        return;
      }
      setOrdersTotal(total);
      setOrders(list);
      try {
        const slipsRes = await packingSlipService.getAll({ limit: 2000, offset: 0 });
        const slips = slipsRes?.data || [];
        const map = {};
        slips.forEach((s) => {
          if (s?.work_order_id != null) map[String(s.work_order_id)] = s;
        });
        setPackingSlipByOrderId(map);
      } catch (slipErr) {
        console.warn('Could not preload packing slips for order actions:', slipErr);
        setPackingSlipByOrderId({});
      }
      setSelectedOrderForHeader(list.length > 0 ? list[0] : null);
    } catch (err) {
      console.error('Error loading orders:', err);
      setError(err.response?.data?.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [
    statusFilter,
    customerFilter,
    partFilter,
    showCancelledOrders,
    showSupersededOrders,
    debouncedTableSearch,
    page,
    rowsPerPage
  ]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const getExistingPackingSlip = async (orderId) => {
    if (!orderId) return null;
    const existingRes = await packingSlipService.getAll({
      work_order_id: orderId,
      limit: 1,
      offset: 0
    });
    const existing = existingRes?.data?.[0] ?? existingRes?.data?.rows?.[0] ?? null;
    return existing;
  };

  const handleGeneratePackingSlip = async (order) => {
    try {
      setPackingSlipBusyOrderId(order?.id ?? null);
      const existing = await getExistingPackingSlip(order?.id);
      if (existing?.id) {
        navigate(`/packing-slips/${existing.id}`);
      } else {
        // Create flow: user sets ship date / tracking on the creation page.
        navigate(`/packing-slips/new?work_order_id=${order.id}`);
      }
    } catch (err) {
      console.error('Generate packing slip error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to generate packing slip');
    } finally {
      setPackingSlipBusyOrderId(null);
    }
  };

  const handleOpenOrderRevisionDialog = async (order) => {
    setRevisionDialogTitle(`Revision History: ${order.identifier || order.work_order_number}`);
    setRevisionDialogType('order');
    setRevisionDialogItems([]);
    setRevisionDialogLoading(true);
    setRevisionDialogOpen(true);
    try {
      const res = await orderService.getRevisions(order.id);
      setRevisionDialogItems(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to load order revision history:', err);
      setRevisionDialogItems([]);
    } finally {
      setRevisionDialogLoading(false);
    }
  };

  const handleOpenQuoteRevisionDialog = async (order) => {
    if (!order.quote_id) return;
    const quoteLabel = order.quote_identifier || order.quote_number || order.quote_id;
    setRevisionDialogTitle(`Revision History: ${quoteLabel}`);
    setRevisionDialogType('quote');
    setRevisionDialogItems([]);
    setRevisionDialogLoading(true);
    setRevisionDialogOpen(true);
    try {
      const res = await quoteService.getRevisions(order.quote_id);
      setRevisionDialogItems(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to load quote revision history:', err);
      setRevisionDialogItems([]);
    } finally {
      setRevisionDialogLoading(false);
    }
  };

  const handlePrintOrder = (order) => {
    if (!order?.id) return;
    navigate(`/work-orders/${order.id}?print=1`);
  };

  const handlePrintPackingSlip = async (order) => {
    try {
      setPackingSlipBusyOrderId(order?.id ?? null);
      const existing = await getExistingPackingSlip(order?.id);
      if (existing?.id) {
        navigate(`/packing-slips/${existing.id}?print=1`);
      } else {
        // Packing slips are immutable after creation; send user to create page first.
        navigate(`/packing-slips/new?work_order_id=${order.id}`);
      }
    } catch (err) {
      console.error('Print packing slip error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to print packing slip');
    } finally {
      setPackingSlipBusyOrderId(null);
    }
  };

  const handleOrderClick = (orderId) => {
    navigate(`/work-orders/${orderId}`);
  };

  const handleOrderRowClick = (order) => {
    setSelectedOrderForHeader(order);
  };

  const handleMenuOpen = (event, order) => {
    event.stopPropagation();
    setSelectedOrder(order);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteFromMenu = () => {
    if (!selectedOrder) return;
    setDeleteDialogOpen(true);
    setAnchorEl(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedOrder?.id) return;
    try {
      setDeleting(true);
      setError('');
      await orderService.delete(selectedOrder.id);
      setDeleteDialogOpen(false);
      setSelectedOrder(null);
      await loadOrders();
    } catch (err) {
      console.error('Error deleting work order:', err);
      setError(err.response?.data?.message || 'Failed to delete work order');
    } finally {
      setDeleting(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'info',
      waiting_external: 'warning',
      in_progress: 'warning',
      Partial: 'warning',
      'Ready to Ship': 'warning',
      shipped: 'primary',
      completed: 'success',
      cancelled: 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusChipSx = (status) => {
    if (status === 'open') {
      return { textTransform: 'uppercase', backgroundColor: '#FFE0B2', color: '#E65100', fontWeight: 700 };
    }
    if (status === 'waiting_external') {
      return { textTransform: 'uppercase', backgroundColor: '#FFF3E0', color: '#EF6C00', fontWeight: 700 };
    }
    if (status === 'Partial') {
      return { textTransform: 'uppercase', backgroundColor: '#FFF9C4', color: '#F57F17', fontWeight: 700 };
    }
    if (status === 'Ready to Ship') {
      return { textTransform: 'uppercase', backgroundColor: '#C8E6C9', color: '#1B5E20', fontWeight: 700 };
    }
    if (status === 'shipped') {
      return { textTransform: 'uppercase', backgroundColor: '#BBDEFB', color: '#0D47A1', fontWeight: 700 };
    }
    return { textTransform: 'capitalize' };
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return formatDateOnly(dateString, 'MM/dd/yyyy') || '';
  };

  const formatOrderDate = (order) => {
    if (order?.order_date) return formatDateOnly(order.order_date, 'EEE M/d') || '—';
    if (order?.created_at) return format(new Date(order.created_at), 'EEE M/d');
    return '—';
  };

  const formatOrderTime = (order) => {
    const dt = order.created_at;
    if (!dt) return '—';
    try {
      return format(new Date(dt), 'h:mma');
    } catch {
      return '—';
    }
  };

  const selectedOrderHasPackingSlip = !!(selectedOrder && packingSlipByOrderId[String(selectedOrder.id)]);
  const selectedOrderHasInvoice = !!selectedOrder?.customer_invoice_id;
  const selectedOrderDeleteBlocked = selectedOrderHasPackingSlip || selectedOrderHasInvoice;
  const selectedOrderDeleteBlockedReason = selectedOrderHasPackingSlip && selectedOrderHasInvoice
    ? 'Cannot delete: order has a packing slip and an invoice.'
    : selectedOrderHasPackingSlip
      ? 'Cannot delete: order has a packing slip.'
      : selectedOrderHasInvoice
        ? 'Cannot delete: order has an invoice.'
        : '';

  /** Tighter than MUI `size="small"` alone — padding, type scale, chips, row actions. */
  const ordersTableDenseSx = {
    '& .MuiTableCell-root': {
      py: 0.35,
      px: '8px',
      fontSize: '0.75rem',
      lineHeight: 1.2,
      verticalAlign: 'middle'
    },
    '& .MuiTableCell-head': {
      py: 0.45,
      fontSize: '0.7rem'
    },
    '& .MuiTableCell-head .MuiBox-root': { pr: 1 },
    '& .MuiChip-root': {
      height: 20,
      maxWidth: '100%',
      '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem', lineHeight: 1.1 }
    },
    '& .MuiIconButton-root': { p: 0.25 },
    '& .MuiButton-sizeSmall': {
      py: 0.125,
      px: 0.75,
      fontSize: '0.75rem',
      lineHeight: 1.2,
      minHeight: 24
    }
  };

  return (
    <Box className="no-print-work-orders">
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h5" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          Orders
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => navigate('/quotes/new')}
          >
            New Quote
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              setNewTmDialogOpen(true);
              setError('');
            }}
          >
            New order
          </Button>
        <Box>
          <IconButton size="small" color="primary" title="Print" onClick={(e) => setPrintMenuAnchor(e.currentTarget)}>
            <Print fontSize="small" />
          </IconButton>
          <IconButton size="small" color="primary" title="Column widths" onClick={() => setColumnsDialogOpen(true)}>
            <ViewColumn fontSize="small" />
          </IconButton>
          <Menu anchorEl={printMenuAnchor} open={!!printMenuAnchor} onClose={() => setPrintMenuAnchor(null)} disableEnforceFocus disableRestoreFocus>
            <MenuItem onClick={() => { printWithFilename('Work-Orders'); setPrintMenuAnchor(null); }}>Print list</MenuItem>
            <MenuItem
              onClick={() => {
                if (selectedOrderForHeader?.id) navigate(`/work-orders/${selectedOrderForHeader.id}`);
                setPrintMenuAnchor(null);
              }}
              disabled={!selectedOrderForHeader}
            >
              Reprint selected
            </MenuItem>
          </Menu>
        </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5, py: 0.5 }}>
          {error}
        </Alert>
      )}

      {/* Order Information Bar Header */}
        <Paper sx={{ p: 1.5, mb: 1.5, backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <Box>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>
                Order #
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {selectedOrderForHeader?.identifier || selectedOrderForHeader?.work_order_number || '—'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>
                Date Entered
              </Typography>
              <Typography variant="body2">
                {selectedOrderForHeader ? formatDate(selectedOrderForHeader.order_date) || '—' : '—'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>
                Customer PO
              </Typography>
              <Typography variant="body2">
                {selectedOrderForHeader?.po_number || '—'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>
                Customer
              </Typography>
              <Typography variant="body2">
                {selectedOrderForHeader?.customer_id ? (
                  <Button
                    size="small"
                    variant="text"
                    sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                    onClick={() => navigate(`/customers/${selectedOrderForHeader.customer_id}`)}
                  >
                    {selectedOrderForHeader.company_name || [selectedOrderForHeader.customer_first_name, selectedOrderForHeader.customer_last_name].filter(Boolean).join(' ') || `#${selectedOrderForHeader.customer_id}`}
                  </Button>
                ) : (
                  selectedOrderForHeader
                    ? (selectedOrderForHeader.company_name || [selectedOrderForHeader.customer_first_name, selectedOrderForHeader.customer_last_name].filter(Boolean).join(' ') || '—')
                    : '—'
                )}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>
                Order Total
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {selectedOrderForHeader ? formatCurrency(selectedOrderForHeader.total_amount, '—') : '—'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>
                Quoted By
              </Typography>
              <Typography variant="body2">
                {selectedOrderForHeader?.salesman_first_name && selectedOrderForHeader?.salesman_last_name
                  ? `${selectedOrderForHeader.salesman_first_name} ${selectedOrderForHeader.salesman_last_name}`
                  : '—'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>
                Quote #
              </Typography>
              <Typography variant="body2">
                {selectedOrderForHeader?.quote_identifier || selectedOrderForHeader?.quote_number || '—'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>
                Status
              </Typography>
              <Chip
                label={selectedOrderForHeader?.status || '—'}
                color={getStatusColor(selectedOrderForHeader?.status)}
                size="small"
                sx={getStatusChipSx(selectedOrderForHeader?.status)}
              />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>
                Due Date
              </Typography>
              <Typography variant="body2">
                {selectedOrderForHeader?.due_date ? formatDate(selectedOrderForHeader.due_date) : '—'}
              </Typography>
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 200 }}>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>
                Description/Notes
              </Typography>
              <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {selectedOrderForHeader?.description || selectedOrderForHeader?.notes || '—'}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => setSelectedOrderForHeader(null)}
              disabled={!selectedOrderForHeader}
              sx={{ ml: 'auto' }}
              title="Clear selection"
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </Paper>

      {/* Search and Filters */}
      <Paper sx={{ p: 1, mb: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            color="success"
            startIcon={<FilterList />}
            size="small"
          >
            Search Options
          </Button>
          <Typography variant="body2" sx={{ flexGrow: 1 }}>
            {ordersTotal === 0
              ? 'No orders match the current filters.'
              : orders.length === 0
                ? `Page ${page + 1} · ${ordersTotal} order${ordersTotal === 1 ? '' : 's'} total`
                : `Showing ${page * rowsPerPage + 1}–${page * rowsPerPage + orders.length} of ${ordersTotal} order${ordersTotal === 1 ? '' : 's'}`}
            {customerFilter ? ' · filtered by customer' : ''}
            {partFilter ? ' · filtered by part' : ''}
            {showCancelledOrders ? ' · including cancelled' : ''}
            {showSupersededOrders ? ' · including prior revisions' : ''}
            {debouncedTableSearch ? ' · table filtered by search (all orders)' : ''}
          </Typography>
          <Button variant="outlined" size="small" startIcon={<FilterList />}>
            Recent
          </Button>
          <Button variant="outlined" size="small" startIcon={<Print />}>
            Reprint
          </Button>
        </Box>
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 280, maxWidth: 500 }}>
            <TextField
              size="small"
              fullWidth
              value={tableSearch}
              onChange={(e) => setTableSearch(e.target.value)}
              placeholder="Search all orders — ID, customer, PO, quote #, notes, status, total, date, salesman…"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: tableSearch ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="Clear search"
                      onClick={() => setTableSearch('')}
                      edge="end"
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined
              }}
            />
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ViewColumn />}
            onClick={() => setColumnsDialogOpen(true)}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Columns
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              Filter by date:
            </Typography>
            <TextField
              size="small"
              type="date"
              placeholder="From"
              value={colDateFrom}
              onChange={(e) => setColDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 150, '& .MuiInputBase-input': { minWidth: 130 } }}
              inputProps={{ 'aria-label': 'Date from' }}
            />
            <Typography variant="body2" color="text.secondary">to</Typography>
            <TextField
              size="small"
              type="date"
              placeholder="To"
              value={colDateTo}
              onChange={(e) => setColDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 150, '& .MuiInputBase-input': { minWidth: 130 } }}
              inputProps={{ 'aria-label': 'Date to' }}
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={showCancelledOrders}
                  onChange={(e) => {
                    const v = e.target.checked;
                    setShowCancelledOrders(v);
                    try {
                      window.localStorage.setItem('wm_orders_show_cancelled', v ? 'true' : 'false');
                    } catch {
                      /* ignore */
                    }
                  }}
                  size="small"
                />
              )}
              label={<Typography variant="body2">Show cancelled</Typography>}
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={showSupersededOrders}
                  onChange={(e) => {
                    const v = e.target.checked;
                    setShowSupersededOrders(v);
                    try {
                      window.localStorage.setItem('wm_orders_show_superseded', v ? 'true' : 'false');
                    } catch {
                      /* ignore */
                    }
                  }}
                  size="small"
                />
              )}
              label={<Typography variant="body2">Show prior revisions</Typography>}
            />
          </Box>
        </Box>
      </Paper>

      {/* Orders Table */}
      <TableContainer component={Paper} sx={{ maxHeight: '60vh' }}>
        <Table size="small" stickyHeader sx={ordersTableDenseSx}>
          <TableHead>
            <TableRow>
              <ResizableHeaderCell width={colWidths.order} onResizeStart={(e) => startResize('order', e)}>
                <TableSortLabel active={sortKey === 'order_identifier'} direction={sortDirection} onClick={() => handleSort('order_identifier')}>
                  Order
                </TableSortLabel>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.quote} onResizeStart={(e) => startResize('quote', e)}>
                <TableSortLabel active={sortKey === 'quote_identifier'} direction={sortDirection} onClick={() => handleSort('quote_identifier')}>
                  Quote #
                </TableSortLabel>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.date} onResizeStart={(e) => startResize('date', e)}>
                <TableSortLabel active={sortKey === 'order_date'} direction={sortDirection} onClick={() => handleSort('order_date')}>
                  Date
                </TableSortLabel>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.time} onResizeStart={(e) => startResize('time', e)}>
                <TableSortLabel active={sortKey === 'time'} direction={sortDirection} onClick={() => handleSort('time')}>
                  Time
                </TableSortLabel>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.customer} onResizeStart={(e) => startResize('customer', e)}>
                <TableSortLabel active={sortKey === 'customer_name'} direction={sortDirection} onClick={() => handleSort('customer_name')}>
                  Customer
                </TableSortLabel>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.customerPo} onResizeStart={(e) => startResize('customerPo', e)}>
                <TableSortLabel active={sortKey === 'customer_po'} direction={sortDirection} onClick={() => handleSort('customer_po')}>
                  Customer PO
                </TableSortLabel>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.total} onResizeStart={(e) => startResize('total', e)}>
                <TableSortLabel active={sortKey === 'total_amount'} direction={sortDirection} onClick={() => handleSort('total_amount')}>
                  Order Total
                </TableSortLabel>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.salesman} onResizeStart={(e) => startResize('salesman', e)}>
                <TableSortLabel active={sortKey === 'salesman'} direction={sortDirection} onClick={() => handleSort('salesman')}>
                  Salesman
                </TableSortLabel>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.status} onResizeStart={(e) => startResize('status', e)}>
                <TableSortLabel active={sortKey === 'status'} direction={sortDirection} onClick={() => handleSort('status')}>
                  Status
                </TableSortLabel>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.notes} onResizeStart={(e) => startResize('notes', e)}>
                <TableSortLabel active={sortKey === 'notes'} direction={sortDirection} onClick={() => handleSort('notes')}>
                  Notes
                </TableSortLabel>
              </ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.actions} onResizeStart={(e) => startResize('actions', e)}>Actions</ResizableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : filteredOrders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  <Typography variant="body2" color="textSecondary">
                    {orders.length === 0 ? 'No orders found' : 'No orders match the date filter on this page.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredOrders.map((order) => {
                const hasPackingSlip = !!packingSlipByOrderId[String(order.id)];
                return (
                <TableRow
                  key={order.id}
                  hover
                  onClick={() => {
                    handleOrderRowClick(order);
                  }}
                  onDoubleClick={() => handleOrderClick(order.id)}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: selectedOrderForHeader?.id === order.id ? 'action.selected' : 'inherit',
                    ...((order.status === 'cancelled' || order.superseded_by_work_order_id != null)
                      ? { opacity: 0.9 }
                      : {})
                  }}
                >
                  <TableCell sx={{ width: colWidths.order }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                      <span>{order.identifier || order.work_order_number}</span>
                      {order.superseded_by_work_order_id != null && (
                        <Tooltip
                          title={`Superseded by ${order.superseded_by_work_order_identifier ?? order.superseded_by_work_order_number ?? `#${order.superseded_by_work_order_id}`}`}
                        >
                          <Chip label="Prior revision" size="small" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: colWidths.quote }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {order.quote_id != null ? (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/quotes/${order.quote_id}`);
                          }}
                          sx={{ textTransform: 'none' }}
                        >
                          {order.quote_identifier ?? order.quote_number ?? order.quote_id}
                        </Button>
                      ) : (
                        '—'
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: colWidths.date }}>{formatOrderDate(order)}</TableCell>
                  <TableCell sx={{ width: colWidths.time }}>{formatOrderTime(order)}</TableCell>
                  <TableCell sx={{ width: colWidths.customer }}>
                    {order.company_name || [order.customer_first_name, order.customer_last_name].filter(Boolean).join(' ') || '-'}
                  </TableCell>
                  <TableCell sx={{ width: colWidths.customerPo }}>{order.po_number || '-'}</TableCell>
                  <TableCell sx={{ width: colWidths.total }}>
                    {formatCurrency(order.total_amount, '-')}
                  </TableCell>
                  <TableCell sx={{ width: colWidths.salesman }}>
                    {order.salesman_first_name && order.salesman_last_name
                      ? `${order.salesman_first_name} ${order.salesman_last_name}`
                      : '-'}
                  </TableCell>
                  <TableCell sx={{ width: colWidths.status }}>
                    <Chip
                      label={order.status || 'open'}
                      size="small"
                      color={getStatusColor(order.status)}
                      sx={getStatusChipSx(order.status)}
                    />
                  </TableCell>
                  <TableCell sx={{ width: colWidths.notes }}>
                    {order.description || order.notes
                      ? (order.description || order.notes).substring(0, 50) + '...'
                      : '-'}
                  </TableCell>
                  <TableCell sx={{ width: colWidths.actions }} onClick={(e) => e.stopPropagation()}>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      <IconButton
                        size="small"
                        color="primary"
                        title="Print order"
                        onClick={(e) => { e.stopPropagation(); handlePrintOrder(order); }}
                      >
                        <Print fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color={hasPackingSlip ? 'success' : 'primary'}
                        title={hasPackingSlip ? 'View packing slip' : 'Generate packing slip'}
                        onClick={(e) => { e.stopPropagation(); handleGeneratePackingSlip(order); }}
                        disabled={packingSlipBusyOrderId === order?.id}
                      >
                        <Receipt fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="primary"
                        title="Print packing slip"
                        onClick={(e) => { e.stopPropagation(); handlePrintPackingSlip(order); }}
                        disabled={packingSlipBusyOrderId === order?.id}
                      >
                        <LocalShipping fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, order)}
                      >
                        <MoreVert />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          rowsPerPageOptions={[50, 100]}
          count={ordersTotal}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Rows per page"
        />
      </TableContainer>

      {/* Print view – work orders list */}
      <PrintPage title="Work Orders" showSpacer={false}>
        <p style={{ fontSize: 12, marginBottom: 8 }}>
          {filteredOrders.length} record(s). Printed {format(new Date(), 'MM/dd/yyyy HH:mm')}
        </p>
        <table className="print-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ padding: 6, textAlign: 'left' }}>Order #</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Quote #</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Date</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Time</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Customer</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Customer PO</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Order Total</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Salesman</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.map((o) => (
              <tr key={o.id}>
                <td style={{ padding: 6 }}>{o.identifier || o.work_order_number}</td>
                <td style={{ padding: 6 }}>{o.quote_identifier ?? o.quote_number ?? '—'}</td>
                <td style={{ padding: 6 }}>{formatOrderDate(o)}</td>
                <td style={{ padding: 6 }}>{formatOrderTime(o)}</td>
                <td style={{ padding: 6 }}>
                  {o.company_name ||
                    [o.customer_first_name, o.customer_last_name].filter(Boolean).join(' ') ||
                    '—'}
                </td>
                <td style={{ padding: 6 }}>{o.po_number || '—'}</td>
                <td style={{ padding: 6 }}>{formatCurrency(o.total_amount, '—')}</td>
                <td style={{ padding: 6 }}>
                  {o.salesman_first_name && o.salesman_last_name
                    ? `${o.salesman_first_name} ${o.salesman_last_name}`
                    : '—'}
                </td>
                <td style={{ padding: 6 }}>{o.status || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </PrintPage>

      {/* Column Width Settings Dialog */}
      <Dialog
        open={columnsDialogOpen}
        onClose={() => setColumnsDialogOpen(false)}
        aria-labelledby="columns-width-dialog-title"
      >
        <DialogTitle id="columns-width-dialog-title">Column widths</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Adjust the column widths for the Orders table.
          </DialogContentText>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {Object.keys(DEFAULT_ORDER_COL_WIDTHS).map((key) => (
              <TextField
                key={key}
                label={`${key} column (px)`}
                type="number"
                size="small"
                value={colWidths[key]}
                onChange={(e) => setColWidths((prev) => ({ ...prev, [key]: Number(e.target.value) || 0 }))}
                inputProps={{ min: 80, step: 10 }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetColWidths} color="inherit">
            Reset
          </Button>
          <Button onClick={() => setColumnsDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              saveColWidths(colWidths);
              setColumnsDialogOpen(false);
            }}
            variant="contained"
            color="primary"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revision History Dialog */}
      <Dialog
        open={revisionDialogOpen}
        onClose={() => setRevisionDialogOpen(false)}
        aria-labelledby="wo-revision-history-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="wo-revision-history-dialog-title">{revisionDialogTitle}</DialogTitle>
        <DialogContent>
          {revisionDialogLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : revisionDialogItems.length === 0 ? (
            <DialogContentText>No previous revisions found.</DialogContentText>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{revisionDialogType === 'order' ? 'Order #' : 'Quote #'}</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {revisionDialogItems.map((rev) => (
                  <TableRow key={rev.id}>
                    <TableCell>{rev.identifier}</TableCell>
                    <TableCell>
                      {revisionDialogType === 'order'
                        ? (rev.order_date ? formatDate(rev.order_date) : '—')
                        : (rev.quote_date ? formatDate(rev.quote_date) : '—')}
                    </TableCell>
                    <TableCell>
                      <Chip label={rev.status || '—'} size="small" color={getStatusColor(rev.status)} sx={{ textTransform: 'capitalize' }} />
                    </TableCell>
                    <TableCell>{formatCurrency(rev.total_amount, '—')}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setRevisionDialogOpen(false);
                          if (revisionDialogType === 'order') {
                            navigate(`/work-orders/${rev.id}`);
                          } else {
                            navigate(`/quotes/${rev.id}`);
                          }
                        }}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevisionDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          if (selectedOrder) handleOrderClick(selectedOrder.id);
          handleMenuClose();
        }}>
          View Details
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>Edit</MenuItem>
        <MenuItem onClick={handleMenuClose}>Copy</MenuItem>
        <MenuItem onClick={handleMenuClose}>Print</MenuItem>
        <MenuItem
          onClick={handleDeleteFromMenu}
          disabled={selectedOrderDeleteBlocked}
          sx={{ color: selectedOrderDeleteBlocked ? 'text.disabled' : 'error.main' }}
          title={selectedOrderDeleteBlockedReason || 'Delete work order'}
        >
          Delete
        </MenuItem>
      </Menu>

      <Dialog
        open={newTmDialogOpen}
        onClose={() => {
          if (!tmSaving) {
            setNewTmDialogOpen(false);
            setNewOrderStyle('standard');
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>New order</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Create an order without a quote. For standard orders, add parts and services on the order page. Field Work / Service orders use the same page for
            description and totals workflow similar to that quote style.
          </DialogContentText>
          <FormControl component="fieldset" variant="standard" sx={{ mb: 1, display: 'block' }}>
            <FormLabel component="legend" sx={{ fontSize: '0.875rem', mb: 0.5 }}>
              Order style
            </FormLabel>
            <RadioGroup
              value={newOrderStyle}
              onChange={(e) => setNewOrderStyle(e.target.value)}
            >
              <FormControlLabel value="standard" control={<Radio size="small" />} label="Standard (line items)" />
              <FormControlLabel
                value="scope_of_work"
                control={<Radio size="small" />}
                label="Field Work / Service"
              />
            </RadioGroup>
          </FormControl>
          <Autocomplete
            options={[NEW_CUSTOMER_OPTION, ...tmCustomers]}
            getOptionLabel={(c) => tmCustomerLabel(c)}
            isOptionEqualToValue={(opt, val) =>
              (opt?.__isNew && val?.__isNew) || (opt && val && opt.id === val.id)
            }
            value={tmCustomer}
            onChange={(e, val) => {
              if (!val) {
                setTmCustomer(null);
                return;
              }
              if (val.__isNew) {
                setNewCustomerModalOpen(true);
                return;
              }
              setTmCustomer(val);
            }}
            renderInput={(params) => <TextField {...params} label="Customer" size="small" margin="normal" required />}
          />
          <Box sx={{ mt: -0.5, mb: 1 }}>
            <Typography variant="body2" color="text.secondary" component="span">
              No customer yet?{' '}
            </Typography>
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={() => setNewCustomerModalOpen(true)}
              sx={{ verticalAlign: 'baseline' }}
            >
              Quick create
            </Link>
            <Typography variant="body2" color="text.secondary" component="span">
              {' · '}
            </Typography>
            <Link component={RouterLink} to="/customers/new" target="_blank" rel="noopener noreferrer" variant="body2">
              Full customer form (new tab)
            </Link>
          </Box>
          <TextField
            fullWidth
            size="small"
            label="Description (optional)"
            value={tmDescription}
            onChange={(e) => setTmDescription(e.target.value)}
            margin="normal"
          />
          <TextField
            fullWidth
            size="small"
            label="Customer PO (optional)"
            value={tmPoNumber}
            onChange={(e) => setTmPoNumber(e.target.value)}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setNewTmDialogOpen(false);
              setNewOrderStyle('standard');
            }}
            disabled={tmSaving}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreateTmOrder} disabled={tmSaving}>
            {tmSaving ? 'Creating…' : 'Create order'}
          </Button>
        </DialogActions>
      </Dialog>

      <NewCustomerModal
        open={newCustomerModalOpen}
        onClose={() => setNewCustomerModalOpen(false)}
        onCreated={(created) => {
          setNewCustomerModalOpen(false);
          if (created?.id) {
            setTmCustomers((prev) => {
              const rest = prev.filter((c) => c.id !== created.id);
              const next = [...rest, created];
              next.sort((a, b) => tmCustomerLabel(a).localeCompare(tmCustomerLabel(b), undefined, { sensitivity: 'base' }));
              return next;
            });
            setTmCustomer(created);
          }
        }}
      />

      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Work Order</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selectedOrderDeleteBlocked
              ? `Order ${selectedOrder?.identifier || selectedOrder?.work_order_number || ''} cannot be deleted because it has${selectedOrderHasPackingSlip ? ' a packing slip' : ''}${selectedOrderHasPackingSlip && selectedOrderHasInvoice ? ' and' : ''}${selectedOrderHasInvoice ? ' an invoice' : ''}.`
              : `Delete order ${selectedOrder?.identifier || selectedOrder?.work_order_number || ''}? This action cannot be undone.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleting || selectedOrderDeleteBlocked}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkOrders;

